import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaInstagram,
} from "react-icons/fa";
import { Link } from "react-router-dom";

import heroCommunity from "../../../assets/images/abanderado.jpg";
import heroExpression from "../../../assets/images/_MG_1624.jpg";
import heroExperience from "../../../assets/images/salidas12.jpg";
import initialImage from "../../../assets/images/nivelinicial.jpg";
import primaryImage from "../../../assets/images/primaria.jpg";
import secondaryImage from "../../../assets/images/nivelsecundario.png";
import cambridgeImage from "../../../assets/images/ingles1.jpg";
import sportImage from "../../../assets/images/deportesFondo.jpg";
import artImage from "../../../assets/images/arte3.jpg";
import communityImage from "../../../assets/images/actividad6.jpg";
import instagramOne from "../../../assets/images/noticia1.jpg";
import instagramTwo from "../../../assets/images/noticia2.jpg";
import instagramThree from "../../../assets/images/noticia3.jpg";

import "./HomePreview.css";

const SLIDES = [
  {
    image: heroCommunity,
    position: "center 58%",
    mobilePosition: "72% 52%",
    kicker: "Comunidad educativa",
    title: "Formamos vidas, construimos futuro",
    text: "Una educación cercana que integra conocimiento, valores y experiencias significativas.",
  },
  {
    image: heroExpression,
    position: "center center",
    mobilePosition: "54% center",
    kicker: "Aprendizaje integral",
    title: "Cada voz encuentra su lugar",
    text: "Acompañamos a cada estudiante para que descubra sus capacidades y aprenda con libertad.",
  },
  {
    image: heroExperience,
    position: "52% 44%",
    mobilePosition: "62% center",
    compactTitle: true,
    kicker: "Experiencias que educan",
    title: "El aprendizaje también sucede fuera del aula",
    text: "Proyectos, salidas y actividades que conectan los contenidos con el mundo real.",
  },
];

const LEVELS = [
  {
    title: "Kinder",
    age: "3 a 5 años",
    text: "Un primer encuentro con el aprendizaje a través del juego, la exploración sensorial y el acompañamiento afectivo.",
    tags: ["Juego dirigido", "Bilingüe inicial", "Naturaleza"],
    image: initialImage,
    to: "/homeinicial",
  },
  {
    title: "Primaria",
    age: "6 a 11 años",
    text: "Consolidación de habilidades académicas con proyectos interdisciplinarios que despiertan la curiosidad y el pensamiento crítico.",
    tags: ["Proyectos", "English Cambridge", "Arte y ciencia"],
    image: primaryImage,
    to: "/nivelprimario",
  },
  {
    title: "Secundaria",
    age: "12 a 17 años",
    text: "Formación integral con orientación académica y humana, preparando a los estudiantes para los desafíos del mundo real.",
    tags: ["Orientación", "Internacional", "Vida ciudadana"],
    image: secondaryImage,
    to: "/nivelsecundario",
  },
];

const ACTIVITIES = [
  {
    title: "Educación física",
    text: "Movimiento, disciplina y trabajo en equipo como pilares de la formación.",
    image: sportImage,
    to: "/deportes",
  },
  {
    title: "Arte",
    text: "Música, plástica y teatro para explorar nuevas formas de expresión.",
    image: artImage,
    to: "/arteActividad",
  },
  {
    title: "Comunidad activa",
    text: "Encuentros y proyectos que fortalecen el compromiso con los demás.",
    image: communityImage,
    to: "/actividades",
  },
];

function Hero() {
  const [index, setIndex] = useState(0);
  const move = useCallback((direction) => {
    setIndex((current) => (current + direction + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => move(1), 7000);
    return () => window.clearInterval(timer);
  }, [move]);

  const slide = SLIDES[index];

  return (
    <section className="tc-home-hero" aria-roledescription="carrusel">
      <AnimatePresence initial={false}>
        <motion.img
          key={slide.image}
          src={slide.image}
          alt=""
          style={{
            "--tc-hero-position": slide.position,
            "--tc-hero-position-mobile": slide.mobilePosition,
          }}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 0.8 }, scale: { duration: 7 } }}
        />
      </AnimatePresence>
      <div className="tc-home-hero-shade" />

      <div className="tc-home-container tc-home-hero-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            className={`tc-home-hero-card${slide.compactTitle ? " has-long-title" : ""}`}
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.55 }}
          >
            <span className="tc-home-pill">{slide.kicker}</span>
            <h1>{slide.title}</h1>
            <p>{slide.text}</p>
            <div className="tc-home-actions">
              <Link className="tc-home-button tc-home-button-gold" to="/proyectoEducativo">
                Proyecto educativo <FaArrowRight />
              </Link>
              <Link className="tc-home-button tc-home-button-ghost" to="/nueva-admision">
                Admisiones
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="tc-home-hero-controls">
        <div className="tc-home-dots" aria-label="Seleccionar imagen">
          {SLIDES.map((item, slideIndex) => (
            <button
              key={item.title}
              type="button"
              className={slideIndex === index ? "is-active" : ""}
              aria-label={`Ir a imagen ${slideIndex + 1}`}
              aria-current={slideIndex === index ? "true" : undefined}
              onClick={() => setIndex(slideIndex)}
            />
          ))}
        </div>
        <div className="tc-home-arrows">
          <button type="button" aria-label="Imagen anterior" onClick={() => move(-1)}><FaChevronLeft /></button>
          <button type="button" aria-label="Imagen siguiente" onClick={() => move(1)}><FaChevronRight /></button>
        </div>
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, text, light = false }) {
  return (
    <header className={`tc-home-heading${light ? " is-light" : ""}`}>
      <span>{eyebrow}</span>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </header>
  );
}

export default function HomePreview() {
  return (
    <>
      <main className="tomas-connect-home">
        <Hero />

      <section className="tc-home-section tc-home-levels">
        <div className="tc-home-container">
          <SectionHeading
            eyebrow="Proyecto educativo"
            title="Niveles de formación"
            text="Una propuesta que acompaña cada etapa con cercanía, exigencia y una mirada integral."
          />
          <div className="tc-home-level-grid">
            {LEVELS.map((level, index) => (
              <motion.article
                key={level.title}
                className="tc-home-level-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="tc-home-level-image">
                  <img src={level.image} alt={level.title} />
                  <span>{level.age}</span>
                </div>
                <div>
                  <h3>{level.title}</h3>
                  <p>{level.text}</p>
                  <div className="tc-home-level-tags">
                    {level.tags.map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                  <Link to={level.to}>Conocer el nivel <FaArrowRight /></Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="tc-home-cambridge">
        <img src={cambridgeImage} alt="Programa de inglés intensivo" />
        <div className="tc-home-cambridge-shade" />
        <motion.div
          className="tc-home-container tc-home-cambridge-content"
          initial={{ opacity: 0, x: -25 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.25 }}
        >
          <span className="tc-home-pill">Cambridge English School</span>
          <h2>Inglés intensivo con proyección internacional</h2>
          <p>Una propuesta sostenida para desarrollar herramientas de comunicación y acceder a certificaciones internacionales.</p>
          <Link className="tc-home-button tc-home-button-gold" to="/InglesIntensivo">
            Conocer el programa <FaArrowRight />
          </Link>
        </motion.div>
      </section>

      <section className="tc-home-section tc-home-activities">
        <div className="tc-home-container">
          <SectionHeading
            eyebrow="Experiencias"
            title="Aprender también es hacer"
            text="Actividades que desarrollan la creatividad, el cuerpo, los vínculos y el compromiso."
            light
          />
          <div className="tc-home-activity-grid">
            {ACTIVITIES.map((activity) => (
              <Link key={activity.title} className="tc-home-activity-card" to={activity.to}>
                <img src={activity.image} alt="" />
                <div>
                  <h3>{activity.title}</h3>
                  <p>{activity.text}</p>
                  <span>Conocer más <FaArrowRight /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="tc-home-section tc-home-social">
        <div className="tc-home-container">
          <div className="tc-home-social-copy">
            <SectionHeading
              eyebrow="Vida escolar"
              title="Nuestra comunidad, todos los días"
              text="Celebraciones, proyectos y aprendizajes compartidos dentro y fuera del aula."
            />
            <a
              className="tc-home-button tc-home-button-green"
              href="https://www.instagram.com/santotomassr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram /> Seguinos en Instagram
            </a>
          </div>
          <div className="tc-home-social-grid">
            {[instagramOne, instagramTwo, instagramThree].map((image, index) => (
              <img key={image} src={image} alt={`Actividad escolar ${index + 1}`} />
            ))}
          </div>
        </div>
      </section>

        <section className="tc-home-cta">
        <div className="tc-home-container">
          <div>
            <span>Admisiones</span>
            <h2>Conocé nuestra propuesta educativa</h2>
            <p>Encontrá información sobre el proceso y descargá las planillas de preinscripción.</p>
          </div>
          <Link className="tc-home-button tc-home-button-gold" to="/nueva-admision">
            Ver admisiones <FaArrowRight />
          </Link>
        </div>
        </section>
      </main>
    </>
  );
}
